// ============================================================================
// Luminance + contrast helpers
// ----------------------------------------------------------------------------
// WCAG-style relative luminance and contrast ratio computation. Used to pick
// the more readable text color out of a small candidate set (typically the
// theme's `textOnPrimary` and `textOnSecondary` tokens) for a given background
// surface.
//
// The hex parser accepts `#rgb`, `#rrggbb`, `rgb(...)` and `rgba(...)` strings
// — i.e. anything `getComputedStyle()` is likely to return for a CSS variable
// — and falls back to `null` for unsupported formats so callers can keep
// their existing default.
// ============================================================================

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/** Parse a CSS color string (hex with optional alpha, `rgb[a](...)`, `hsl[a](...)`). Alpha is ignored. */
export function parseColor(input: string | null | undefined): RGB | null {
  if (!input) return null;
  const s = input.trim();

  // #rgb
  let m = s.match(/^#([0-9a-f]{3})$/i);
  if (m) {
    const h = m[1];
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }

  // #rgba
  m = s.match(/^#([0-9a-f]{3})[0-9a-f]$/i);
  if (m) return parseColor(`#${m[1]}`);

  // #rrggbbaa
  m = s.match(/^#([0-9a-f]{6})[0-9a-f]{2}$/i);
  if (m) return parseColor(`#${m[1]}`);

  // hsl(h, s%, l%) / hsla(...) / space-separated hsl(h s% l%)
  m = s.match(/^hsla?\(\s*(-?\d+(?:\.\d+)?)(?:deg)?\s*[,\s]\s*(\d+(?:\.\d+)?)%\s*[,\s]\s*(\d+(?:\.\d+)?)%/i);
  if (m) {
    const h = (((parseFloat(m[1]) % 360) + 360) % 360) / 360;
    const sat = parseFloat(m[2]) / 100;
    const l = parseFloat(m[3]) / 100;
    const q = l < 0.5 ? l * (1 + sat) : l + sat - l * sat;
    const p = 2 * l - q;
    const channel = (t: number) => {
      const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
      if (tt < 1 / 6) return p + (q - p) * 6 * tt;
      if (tt < 1 / 2) return q;
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
      return p;
    };
    return {
      r: Math.round(channel(h + 1 / 3) * 255),
      g: Math.round(channel(h) * 255),
      b: Math.round(channel(h - 1 / 3) * 255),
    };
  }

  // #rrggbb
  m = s.match(/^#([0-9a-f]{6})$/i);
  if (m) {
    const h = m[1];
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  // rgb(r, g, b) / rgba(r, g, b, a) — also handles the modern space-separated
  // `rgb(r g b)` syntax that getComputedStyle emits in newer engines.
  m = s.match(/^rgba?\(\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)/i);
  if (m) {
    return {
      r: Math.round(parseFloat(m[1])),
      g: Math.round(parseFloat(m[2])),
      b: Math.round(parseFloat(m[3])),
    };
  }

  return null;
}

/** WCAG relative luminance for an sRGB color in the range [0, 1]. */
export function relativeLuminance(input: string | RGB | null | undefined): number {
  const rgb = typeof input === 'string' || input == null ? parseColor(input as string) : input;
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG contrast ratio between two colors. Returns a number in [1, 21]. */
export function contrastRatio(
  a: string | RGB | null | undefined,
  b: string | RGB | null | undefined,
): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** WCAG contrast ratio between two pre-computed relative luminances. */
export function luminanceContrast(la: number, lb: number): number {
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Resolve any CSS color expression (including `var(--token)`) to the
 * browser's computed value by painting it on a detached probe element.
 * Client-only; returns null during SSR.
 */
export function resolveCssColor(value: string): string | null {
  if (typeof document === 'undefined' || !document.body) return null;
  const probe = document.createElement('span');
  probe.style.display = 'none';
  probe.style.color = value;
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();
  return computed || null;
}

/**
 * Average WCAG relative luminance of an image's visible (mostly opaque)
 * pixels, sampled on a small canvas. Resolves null when the image can't be
 * read (CORS-tainted canvas, load error, fully transparent image).
 */
export function measureImageLuminance(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      try {
        const size = 48;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let total = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 128) continue;
          total += relativeLuminance({ r: data[i], g: data[i + 1], b: data[i + 2] });
          count += 1;
        }
        resolve(count > 0 ? total / count : null);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/**
 * From a set of candidate text colors, return the one that has the highest
 * WCAG contrast ratio against the given background. Ties go to the first
 * candidate. Unparseable candidates are skipped.
 */
export function pickHighestContrast<T extends string>(
  background: string | null | undefined,
  candidates: readonly T[],
): T | null {
  let best: T | null = null;
  let bestRatio = -Infinity;
  for (const candidate of candidates) {
    if (!parseColor(candidate)) continue;
    const ratio = contrastRatio(background, candidate);
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
  }
  return best;
}

function toHex({ r, g, b }: RGB): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/**
 * Per-channel sRGB mix, matching CSS `color-mix(in srgb, a <weightA>, b)`.
 * `weightA` is in [0, 1]. Returns a `#rrggbb` string, or `null` if either
 * input can't be parsed.
 */
export function mixColors(
  a: string | null | undefined,
  b: string | null | undefined,
  weightA: number,
): string | null {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return null;
  const w = Math.max(0, Math.min(1, weightA));
  return toHex({
    r: ca.r * w + cb.r * (1 - w),
    g: ca.g * w + cb.g * (1 - w),
    b: ca.b * w + cb.b * (1 - w),
  });
}

/**
 * Pick the highest-contrast text color for `background`. If even the best
 * candidate misses `minRatio` (WCAG AA body text = 4.5), step the background
 * toward black (light text) or white (dark text) until it clears, so
 * mid-tone brand colors still yield a readable surface + text pair.
 */
export function ensureReadablePair(
  background: string | null | undefined,
  candidates: readonly string[],
  minRatio = 4.5,
): { background: string; text: string } | null {
  const base = parseColor(background);
  if (!base) return null;
  const baseHex = toHex(base);
  const text = pickHighestContrast(baseHex, candidates);
  if (!text) return null;
  if (contrastRatio(baseHex, text) >= minRatio) return { background: baseHex, text };

  const textIsLight = relativeLuminance(text) > relativeLuminance(baseHex);
  const target = textIsLight ? '#000000' : '#ffffff';
  for (let step = 0.05; step <= 1; step += 0.05) {
    const adjusted = mixColors(target, baseHex, step) as string;
    if (contrastRatio(adjusted, text) >= minRatio) return { background: adjusted, text };
  }
  return { background: target, text };
}
